import {
  GuestEnvironmentCapabilities,
  GuestEnvironmentList,
  GuestEnvironment,
  GuestEnvironmentError,
  CreateGuestEnvironment,
  GuestEnvironmentAction,
  GuestProvisioningGrant,
  GuestActionGrant,
} from "./guestEnvironment.ts";
import {
  PreviewAnnotationSubmissionResultSchema,
  PreviewCancelPickInput,
  PreviewPickElementInput,
} from "./ipc.ts";
import {
  ComputerUseServiceError,
  ComputerUseServiceStatus,
  MacosPrivacyPane,
} from "./computerUseService.ts";
import { McpConnectionTestInput, McpConnectionTestResult } from "./mcpObservation.ts";
import { McpObservationInput, McpObservationError, McpObservations } from "./mcpObservation.ts";
import {
  ProviderConsumeResetCreditInput,
  ProviderConsumeResetCreditResult,
  ProviderUsageLimitsError,
} from "./providerUsageLimits.ts";
import { UsagePricing, UsageReadError, UsageSummary, UsageSummaryInput } from "./usage.ts";
import {
  PullRequestDiscussionInput,
  PullRequestEditInput,
  PullRequestEditResult,
} from "./pullRequestEdits.ts";
import * as Schema from "effect/Schema";
import * as Rpc from "effect/unstable/rpc/Rpc";
import * as RpcGroup from "effect/unstable/rpc/RpcGroup";
import { CommandId, TrimmedNonEmptyString, ProjectId, NonNegativeInt } from "./baseSchemas.ts";

import { ExternalLauncherError, LaunchEditorInput } from "./editor.ts";
import {
  FilesystemBrowseInput,
  FilesystemBrowseResult,
  FilesystemBrowseError,
} from "./filesystem.ts";
import {
  AssetAccessError,
  AssetCreateUrlInput,
  AssetCreateUrlResult,
  AttachmentCreateUploadUrlInput,
  AttachmentCreateUploadUrlResult,
  AttachmentDeleteInput,
  AttachmentUploadSigningKeyError,
} from "./assets.ts";
import {
  ExperimentEvidenceQueryError,
  ExperimentEvidenceQueryInput,
  ExperimentEvidenceQueryResult,
  ExperimentWorkspaceQueryError,
  ExperimentWorkspaceQueryInput,
  ExperimentWorkspaceQueryResult,
} from "./experiment.ts";
import {
  WorkflowEnqueueRunInput,
  WorkflowError,
  WorkflowReadOutputInput,
  WorkflowReadOutputResult,
  WorkflowRun,
  WorkflowRunIdInput,
  WorkflowSaveInput,
  WorkflowSaveResult,
  WorkflowSetValuesInput,
  WorkflowSetValuesResult,
  WorkflowStreamEvent,
  WorkflowSubscribeInput,
} from "./workflow.ts";
import {
  GitActionProgressEvent,
  VcsSwitchRefInput,
  VcsSwitchRefResult,
  GitCommandError,
  VcsCreateRefInput,
  VcsCreateRefResult,
  VcsCreateWorktreeInput,
  VcsCreateWorktreeResult,
  VcsInitInput,
  VcsListRefsInput,
  VcsListRefsResult,
  GitManagerServiceError,
  GitPreparePullRequestThreadInput,
  GitPreparePullRequestThreadResult,
  VcsPullInput,
  GitPullRequestRefInput,
  VcsPullResult,
  VcsRemoveWorktreeInput,
  GitResolvePullRequestResult,
  GitRunStackedActionInput,
  VcsStatusInput,
  VcsStatusResult,
  VcsStatusStreamEvent,
} from "./git.ts";
import {
  AvailableProjectWorktreesInput,
  AvailableProjectWorktreesResult,
  ManagedThreadWorktreeDeleteAllInput,
  ManagedThreadWorktreeCreateInput,
  ManagedThreadWorktreeCreateResult,
  ManagedThreadWorktreeDeleteInput,
  ManagedThreadWorktreeDeleteProjectInput,
  ManagedThreadWorktreeDeleteResult,
  ManagedThreadWorktreeError,
  ManagedThreadWorktreeDiscardEvidenceInput,
  ManagedThreadWorktreeDiscardEvidenceResult,
  ManagedThreadWorktreeEvidenceRecoveryError,
  ManagedThreadWorktreeListEvidenceRecoveriesInput,
  ManagedThreadWorktreeListEvidenceRecoveriesResult,
  ManagedThreadWorktreeRestoreEvidenceInput,
  ManagedThreadWorktreeRestoreEvidenceResult,
  ManagedThreadWorktreesListInput,
  ManagedThreadWorktreesListResult,
} from "./worktrees.ts";
import {
  ReviewDiffPreviewError,
  ReviewDiffPreviewInput,
  ReviewDiffPreviewResult,
} from "./review.ts";
import { KeybindingsConfigError } from "./keybindings.ts";
import {
  ClientOrchestrationCommand,
  EnvironmentCommandIdempotencyConflictError,
  EnvironmentCommandRejectedError,
  ORCHESTRATION_WS_METHODS,
  OrchestrationDispatchCommandError,
  OrchestrationGetFullThreadDiffError,
  OrchestrationGetFullThreadDiffInput,
  OrchestrationGetSnapshotError,
  OrchestrationThreadHistoryError,
  OrchestrationGetTurnDiffError,
  OrchestrationGetTurnDiffInput,
  OrchestrationReplayEventsError,
  OrchestrationReplayEventsInput,
  OrchestrationRpcSchemas,
  OrchestrationShellBootstrapError,
} from "./orchestration.ts";
import { ProviderInstanceId } from "./providerInstance.ts";
import {
  ContributionsError,
  ContributionsExportInput,
  ContributionsExportResult,
  ContributionsQueryInput,
  ContributionsQueryResult,
} from "./contributions.ts";
import {
  ProjectContributionGeneration,
  ProjectContributionGenerationError,
  ProjectContributionGenerationInput,
} from "./projectContributionSnapshot.ts";
import {
  ProjectAssessAfarInput,
  ProjectAssessAfarResult,
  ProjectActionReadinessEvent,
  ProjectCreateAfarError,
  ProjectCreateAfarInput,
  ProjectDiscoverDesignSystemMentionsError,
  ProjectDiscoverDesignSystemMentionsInput,
  ProjectDiscoverDesignSystemMentionsResult,
  ProjectListEntriesError,
  ProjectListEntriesInput,
  ProjectListEntriesResult,
  ProjectInspectAfarError,
  ProjectInspectAfarInput,
  ProjectInspectAfarResult,
  ProjectReadFileError,
  ProjectReadFileInput,
  ProjectReadFileResult,
  ProjectRefreshEntriesError,
  ProjectRefreshEntriesInput,
  ProjectRefreshEntriesResult,
  ProjectSearchEntriesError,
  ProjectSearchEntriesInput,
  ProjectSearchEntriesResult,
  ProjectWriteFileError,
  ProjectWriteFileInput,
  ProjectWriteFileResult,
  ProjectFileChange,
} from "./project.ts";
import {
  TerminalAttachInput,
  TerminalAttachStreamEvent,
  TerminalClearInput,
  TerminalCloseInput,
  TerminalError,
  TerminalEvent,
  TerminalMetadataStreamEvent,
  TerminalOpenInput,
  TerminalResizeInput,
  TerminalRestartInput,
  TerminalSessionSnapshot,
  TerminalWriteInput,
} from "./terminal.ts";
import {
  DiscoveredLocalServerList,
  BrowserProfileError,
  PreviewBrowserError,
  PreviewCloseInput,
  PreviewError,
  PreviewEvent,
  PreviewClearBrowsingDataInput,
  PreviewHistoryInput,
  PreviewSetColorSchemeInput,
  PreviewSetZoomInput,
  PreviewListInput,
  PreviewListResult,
  PreviewNavigateInput,
  PreviewOpenInput,
  PreviewRefreshInput,
  PreviewReportStatusInput,
  PreviewResizeInput,
  PreviewSessionSnapshot,
  ConfiguredLocalServerUrls,
} from "./preview.ts";
import {
  PreviewAutomationError,
  PreviewAutomationHost,
  PreviewAutomationHostFocus,
  PreviewAutomationResponse,
  PreviewAutomationStreamEvent,
} from "./previewAutomation.ts";
import { HostResourcesSnapshot } from "./hostResources.ts";
import { MachinePoolAddInput, MachinePoolEntryView, MachinePoolError } from "./afarNetwork.ts";
import {
  AppOwnedCodexApiKeyLoginInput,
  AppOwnedCodexAuthenticationCancelInput,
  AppOwnedCodexAuthenticationError,
  AppOwnedCodexAuthenticationResult,
  AppOwnedCodexBrowserLoginInput,
  AppOwnedCodexDeviceCodeLoginInput,
  AppOwnedCodexLogoutInput,
  ServerConfigStreamEvent,
  ServerConfig,
  ManagedProviderCreateInput,
  ManagedProviderDeleteInput,
  ManagedProviderMutationResult,
  ManagedProviderOperationError,
  ManagedProviderRetryInput,
  ServerLifecycleStreamEvent,
  ServerRemoveKeybindingInput,
  ServerRemoveKeybindingResult,
  ServerProviderUpdatedPayload,
  ServerProviderUpdateError,
  ServerProviderUpdateInput,
  ServerProviderUpdateResult,
  ServerProviderSkillCatalogError,
  ServerProviderSkillCatalogInput,
  ServerProviderSkillCatalogResult,
  ServerTraceDiagnosticsResult,
  ServerProcessDiagnosticsResult,
  ServerProcessResourceHistoryInput,
  ServerProcessResourceHistoryResult,
  ServerSignalProcessInput,
  ServerSignalProcessResult,
  ServerUpsertKeybindingInput,
  ServerUpsertKeybindingResult,
} from "./server.ts";
import {
  ServerSettings,
  ServerSettingsError,
  ServerSettingsHostOnlyError,
  ServerSettingsManagedError,
  ServerSettingsPatch,
} from "./settings.ts";
import {
  SourceControlCloneRepositoryInput,
  SourceControlCloneRepositoryResult,
  SourceControlDiscoveryResult,
  GitHubAuthenticationStatus,
  GitHubAuthenticationStartResult,
  GitHubAuthenticationError,
  SourceControlPublishRepositoryInput,
  SourceControlPublishRepositoryResult,
  PullRequestListInput,
  PullRequestListResult,
  PullRequestChecksInput,
  PullRequestChecksResult,
  PullRequestListStatsInput,
  PullRequestListStatsResult,
  PullRequestDetailInput,
  PullRequestDetailResult,
  PullRequestCodeInput,
  PullRequestCodeResult,
  PullRequestTimelineInput,
  PullRequestTimelineResult,
  SourceControlRepositoryError,
  SourceControlRepositoryInfo,
  SourceControlRepositoryLookupInput,
} from "./sourceControl.ts";
import {
  PullRequestLinkedThreadsError,
  PullRequestLinkedThreadsInput,
  PullRequestLinkedThreadsResult,
} from "./threadPullRequests.ts";
import { VcsError } from "./vcs.ts";
import {
  ScheduleActivationError,
  ScheduleListActivationsInput,
  ScheduleListActivationsResult,
  ScheduleListOccurrencesInput,
  ScheduleListOccurrencesResult,
  ScheduleOccurrenceError,
  ScheduleDefinitionError,
  ScheduleDeleteDefinitionInput,
  ScheduleDeleteDefinitionResult,
  ScheduleDeleteActivationInput,
  ScheduleDeleteActivationResult,
  ScheduleDeleteOccurrenceInput,
  ScheduleDeleteOccurrenceResult,
  ScheduleListDefinitionsInput,
  ScheduleListDefinitionsResult,
  ScheduleListRunsInput,
  ScheduleListRunsResult,
  ScheduleCancelRunInput,
  ScheduleResolveRunInput,
  ScheduleRunError,
  ScheduleRunNowInput,
  ScheduleRunNowResult,
  ScheduleRunReadModel,
  ScheduleSaveDefinitionInput,
  ScheduleSaveDefinitionResult,
  ScheduleSaveAndEnableInput,
  ScheduleSaveAndEnableResult,
  ScheduleSetActivationDesiredStateInput,
  ScheduleSetActivationDesiredStateResult,
} from "./schedule.ts";
import {
  DesignDeleteCollectionGroupInput,
  DesignDeleteCollectionGroupResult,
  DesignDeleteInvalidSystemInput,
  DesignDeleteInvalidSystemResult,
  DesignDeleteSystemInput,
  DesignDeleteSystemResult,
  DesignDeleteTargetsInput,
  DesignDeleteTargetsResult,
  DesignListSystemsInput,
  DesignListSystemsResult,
  DesignSystemError,
} from "./design.ts";

export const WS_METHODS = {
  // Project registry methods
  projectsList: "projects.list",
  projectsAdd: "projects.add",
  projectsRemove: "projects.remove",
  projectsListEntries: "projects.listEntries",
  projectsRefreshEntries: "projects.refreshEntries",
  projectsAssessAfar: "projects.assessAfar",
  projectsCreateAfar: "projects.createAfar",
  projectsDiscoverDesignSystemMentions: "projects.discoverDesignSystemMentions",
  projectsInspectAfar: "projects.inspectAfar",
  subscribeProjectActionReadiness: "projects.subscribeActionReadiness",
  projectsReadFile: "projects.readFile",
  projectsSearchEntries: "projects.searchEntries",
  projectsWriteFile: "projects.writeFile",
  projectsSubscribeFile: "projects.subscribeFile",

  // Contributions tab: Authorship Logs of a thread's checkout
  contributionsQuery: "contributions.query",
  projectContributionGenerate: "contributions.project.generate",
  projectContributionGenerationEvents: "contributions.project.generationEvents",
  projectContributionCancel: "contributions.project.cancel",
  contributionsExportAgentTrace: "contributions.exportAgentTrace",

  // Portable Schedule definition methods
  scheduleListDefinitions: "schedule.listDefinitions",
  scheduleSaveDefinition: "schedule.saveDefinition",
  scheduleDeleteDefinition: "schedule.deleteDefinition",
  scheduleListActivations: "schedule.listActivations",
  scheduleListOccurrences: "schedule.listOccurrences",
  scheduleSaveAndEnable: "schedule.saveAndEnable",
  scheduleSetActivationDesiredState: "schedule.setActivationDesiredState",
  scheduleDeleteActivation: "schedule.deleteActivation",
  scheduleDeleteOccurrence: "schedule.deleteOccurrence",
  scheduleListRuns: "schedule.listRuns",
  scheduleRunNow: "schedule.runNow",
  scheduleCancelRun: "schedule.cancelRun",
  scheduleResolveRun: "schedule.resolveRun",

  // Design System deterministic mutation methods
  designListSystems: "design.listSystems",
  designDeleteSystem: "design.deleteSystem",
  designDeleteCollectionGroup: "design.deleteCollectionGroup",
  designDeleteInvalidSystem: "design.deleteInvalidSystem",
  designDeleteTargets: "design.deleteTargets",

  // Read-only Experiment evidence projection
  experimentQueryEvidence: "experiment.queryEvidence",
  experimentQueryWorkspace: "experiment.queryWorkspace",

  // Workflows (ADR 0141)
  workflowSubscribe: "workflow.subscribe",
  workflowSave: "workflow.save",
  workflowSetValues: "workflow.setValues",
  workflowEnqueueRun: "workflow.enqueueRun",
  workflowCancelRun: "workflow.cancelRun",
  workflowRemoveRun: "workflow.removeRun",
  workflowReadOutput: "workflow.readOutput",

  // Shell methods
  shellOpenInEditor: "shell.openInEditor",

  // Filesystem methods
  filesystemBrowse: "filesystem.browse",
  assetsCreateUrl: "assets.createUrl",
  attachmentsCreateUploadUrl: "attachments.createUploadUrl",
  attachmentsDelete: "attachments.delete",

  // VCS methods
  vcsPull: "vcs.pull",
  vcsRefreshStatus: "vcs.refreshStatus",
  vcsListRefs: "vcs.listRefs",
  vcsCreateWorktree: "vcs.createWorktree",
  vcsRemoveWorktree: "vcs.removeWorktree",
  vcsCreateRef: "vcs.createRef",
  vcsSwitchRef: "vcs.switchRef",
  vcsInit: "vcs.init",

  // Managed Thread Worktree methods
  worktreesListAvailable: "worktrees.listAvailable",
  worktreesCreate: "worktrees.create",
  worktreesListManaged: "worktrees.listManaged",
  worktreesDelete: "worktrees.delete",
  worktreesDeleteProject: "worktrees.deleteProject",
  worktreesDeleteAll: "worktrees.deleteAll",
  worktreesListEvidenceRecoveries: "worktrees.listEvidenceRecoveries",
  worktreesRestoreEvidence: "worktrees.restoreEvidence",
  worktreesDiscardEvidence: "worktrees.discardEvidence",

  // Git workflow methods
  gitRunStackedAction: "git.runStackedAction",
  gitResolvePullRequest: "git.resolvePullRequest",
  gitPreparePullRequestThread: "git.preparePullRequestThread",

  // Review methods
  reviewGetDiffPreview: "review.getDiffPreview",

  // Terminal methods
  terminalOpen: "terminal.open",
  terminalAttach: "terminal.attach",
  terminalWrite: "terminal.write",
  terminalResize: "terminal.resize",
  terminalClear: "terminal.clear",
  terminalRestart: "terminal.restart",
  terminalClose: "terminal.close",

  // Preview methods
  previewOpen: "preview.open",
  previewNavigate: "preview.navigate",
  previewResize: "preview.resize",
  previewRefresh: "preview.refresh",
  previewClose: "preview.close",
  previewList: "preview.list",
  previewReportStatus: "preview.reportStatus",
  previewHistory: "preview.history",
  previewSetZoom: "preview.setZoom",
  previewPickElement: "preview.pickElement",
  previewSetColorScheme: "preview.setColorScheme",
  previewClearBrowsingData: "preview.clearBrowsingData",
  previewCancelPick: "preview.cancelPick",
  computerUseServiceStatus: "computerUseService.status",
  computerUseServiceRecheck: "computerUseService.recheck",
  computerUseServiceRequestPermissions: "computerUseService.requestPermissions",
  computerUseServiceOpenPrivacySettings: "computerUseService.openPrivacySettings",
  previewAutomationConnect: "previewAutomation.connect",
  previewAutomationRespond: "previewAutomation.respond",
  previewAutomationFocusHost: "previewAutomation.focusHost",

  // Server meta
  serverGetConfig: "server.getConfig",
  serverSetCloudClientAway: "server.setCloudClientAway",
  serverRefreshProviders: "server.refreshProviders",
  serverProviderCreateAppOwnedCodex: "server.provider.createAppOwnedCodex",
  serverProviderRetryInstall: "server.provider.retryInstall",
  serverProviderDelete: "server.provider.delete",
  serverProviderStartBrowserLogin: "server.provider.auth.startBrowserLogin",
  serverProviderStartDeviceCodeLogin: "server.provider.auth.startDeviceCodeLogin",
  serverProviderLoginApiKey: "server.provider.auth.loginApiKey",
  serverProviderCancelAuthentication: "server.provider.auth.cancel",
  serverProviderLogout: "server.provider.auth.logout",
  serverTestMcpConnection: "server.mcp.testConnection",
  serverProviderRefreshMcp: "server.provider.mcp.refresh",
  subscribeProviderMcp: "subscribeProviderMcp",
  serverGetUsageSummary: "server.getUsageSummary",
  serverRefreshUsageRates: "server.refreshUsageRates",
  serverProviderConsumeResetCredit: "server.provider.usage.consumeResetCredit",
  serverUpdateProvider: "server.updateProvider",
  serverListProviderSkills: "server.listProviderSkills",
  serverUpsertKeybinding: "server.upsertKeybinding",
  serverRemoveKeybinding: "server.removeKeybinding",
  serverUpdateSettings: "server.updateSettings",
  serverDiscoverSourceControl: "server.discoverSourceControl",
  serverGitHubAuthenticationStatus: "server.githubAuthentication.status",
  serverGitHubAuthenticationStart: "server.githubAuthentication.start",
  serverGitHubAuthenticationCancel: "server.githubAuthentication.cancel",
  serverGitHubAuthenticationDisconnect: "server.githubAuthentication.disconnect",
  serverGetTraceDiagnostics: "server.getTraceDiagnostics",
  serverGetProcessDiagnostics: "server.getProcessDiagnostics",
  serverGetHostResources: "server.getHostResources",
  guestEnvironmentsCapabilities: "guestEnvironments.capabilities",
  guestEnvironmentsList: "guestEnvironments.list",
  guestEnvironmentsCreate: "guestEnvironments.create",
  guestEnvironmentsAction: "guestEnvironments.action",
  machinePoolList: "machinePool.list",
  machinePoolAdd: "machinePool.add",
  machinePoolRemove: "machinePool.remove",
  machinePoolIssueDelegation: "machinePool.issueDelegation",
  serverGetProcessResourceHistory: "server.getProcessResourceHistory",
  serverSignalProcess: "server.signalProcess",

  // Source control methods
  sourceControlLookupRepository: "sourceControl.lookupRepository",
  sourceControlCloneRepository: "sourceControl.cloneRepository",
  sourceControlPublishRepository: "sourceControl.publishRepository",
  pullRequestsList: "pullRequests.list",
  pullRequestsListStats: "pullRequests.listStats",
  pullRequestsChecks: "pullRequests.checks",
  pullRequestsDetail: "pullRequests.detail",
  pullRequestsDiscuss: "pullRequests.discuss",
  pullRequestsEdit: "pullRequests.edit",
  pullRequestsCode: "pullRequests.code",
  pullRequestsTimeline: "pullRequests.timeline",
  pullRequestsLinkedThreads: "pullRequests.linkedThreads",

  // Streaming subscriptions
  subscribeVcsStatus: "subscribeVcsStatus",
  subscribeTerminalEvents: "subscribeTerminalEvents",
  subscribeTerminalMetadata: "subscribeTerminalMetadata",
  subscribePreviewEvents: "subscribePreviewEvents",
  subscribeDiscoveredLocalServers: "subscribeDiscoveredLocalServers",
  subscribeServerConfig: "subscribeServerConfig",
  subscribeServerLifecycle: "subscribeServerLifecycle",
} as const;

/**
 * Every Environment Protocol method, declared once.
 *
 * The group below is derived from this object, so a method cannot be
 * declared here and missing from the wire surface, or listed there and
 * never defined.
 */
export const WsRpcs = {
  serverUpsertKeybinding: Rpc.make(WS_METHODS.serverUpsertKeybinding, {
    payload: ServerUpsertKeybindingInput,
    success: ServerUpsertKeybindingResult,
    error: Schema.Union([KeybindingsConfigError]),
  }),

  serverRemoveKeybinding: Rpc.make(WS_METHODS.serverRemoveKeybinding, {
    payload: ServerRemoveKeybindingInput,
    success: ServerRemoveKeybindingResult,
    error: Schema.Union([KeybindingsConfigError]),
  }),

  serverGetConfig: Rpc.make(WS_METHODS.serverGetConfig, {
    payload: Schema.Struct({}),
    success: ServerConfig,
    error: Schema.Union([KeybindingsConfigError, ServerSettingsError]),
  }),

  /**
   * Whether this connection's user has gone unused (`CLOUD_UNUSED_WINDOW_MS`). An away connection
   * stays open but no longer keeps a Cloud Workspace run up on its own. Elsewhere it does nothing.
   */
  serverSetCloudClientAway: Rpc.make(WS_METHODS.serverSetCloudClientAway, {
    payload: Schema.Struct({ away: Schema.Boolean }),
    success: Schema.Struct({}),
  }),

  serverRefreshProviders: Rpc.make(WS_METHODS.serverRefreshProviders, {
    payload: Schema.Struct({
      /**
       * When supplied, only refresh this specific provider instance. When
       * omitted, refresh all configured instances — the legacy `refresh()`
       * behaviour retained for transports that still dispatch untargeted
       * refreshes.
       */
      instanceId: Schema.optional(ProviderInstanceId),
      /**
       * Re-read subscription quota as well as provider status. Quota costs a
       * provider round trip per account, so only a client that is about to
       * show it — the Limits view — asks for it; a readiness check does not.
       */
      refreshUsageLimits: Schema.optional(Schema.Boolean),
    }),
    success: ServerProviderUpdatedPayload,
  }),

  serverProviderCreateAppOwnedCodex: Rpc.make(WS_METHODS.serverProviderCreateAppOwnedCodex, {
    payload: ManagedProviderCreateInput,
    success: ManagedProviderMutationResult,
    error: Schema.Union([ManagedProviderOperationError]),
  }),

  serverProviderRetryInstall: Rpc.make(WS_METHODS.serverProviderRetryInstall, {
    payload: ManagedProviderRetryInput,
    success: ManagedProviderMutationResult,
    error: Schema.Union([ManagedProviderOperationError]),
  }),

  serverProviderDelete: Rpc.make(WS_METHODS.serverProviderDelete, {
    payload: ManagedProviderDeleteInput,
    success: ManagedProviderMutationResult,
    error: Schema.Union([ManagedProviderOperationError]),
  }),

  serverProviderStartBrowserLogin: Rpc.make(WS_METHODS.serverProviderStartBrowserLogin, {
    payload: AppOwnedCodexBrowserLoginInput,
    success: AppOwnedCodexAuthenticationResult,
    error: AppOwnedCodexAuthenticationError,
  }),

  serverProviderStartDeviceCodeLogin: Rpc.make(WS_METHODS.serverProviderStartDeviceCodeLogin, {
    payload: AppOwnedCodexDeviceCodeLoginInput,
    success: AppOwnedCodexAuthenticationResult,
    error: AppOwnedCodexAuthenticationError,
  }),

  serverProviderLoginApiKey: Rpc.make(WS_METHODS.serverProviderLoginApiKey, {
    payload: AppOwnedCodexApiKeyLoginInput,
    success: AppOwnedCodexAuthenticationResult,
    error: AppOwnedCodexAuthenticationError,
  }),

  serverProviderCancelAuthentication: Rpc.make(WS_METHODS.serverProviderCancelAuthentication, {
    payload: AppOwnedCodexAuthenticationCancelInput,
    success: AppOwnedCodexAuthenticationResult,
    error: AppOwnedCodexAuthenticationError,
  }),

  serverProviderLogout: Rpc.make(WS_METHODS.serverProviderLogout, {
    payload: AppOwnedCodexLogoutInput,
    success: AppOwnedCodexAuthenticationResult,
    error: AppOwnedCodexAuthenticationError,
  }),

  serverTestMcpConnection: Rpc.make(WS_METHODS.serverTestMcpConnection, {
    payload: McpConnectionTestInput,
    success: McpConnectionTestResult,
    error: McpObservationError,
  }),
  serverProviderRefreshMcp: Rpc.make(WS_METHODS.serverProviderRefreshMcp, {
    payload: McpObservationInput,
    success: Schema.Void,
    error: McpObservationError,
  }),
  subscribeProviderMcp: Rpc.make(WS_METHODS.subscribeProviderMcp, {
    payload: Schema.Struct({}),
    success: McpObservations,
    stream: true,
  }),
  /**
   * One AFAR Environment's pre-aggregated usage for a window. The scan is
   * expensive and cached per file, so concurrent identical requests share it.
   */
  serverGetUsageSummary: Rpc.make(WS_METHODS.serverGetUsageSummary, {
    payload: UsageSummaryInput,
    success: UsageSummary,
    error: UsageReadError,
  }),
  /**
   * Refetches the model rate table ahead of its TTL, so a model the table
   * gained since the last fetch gets priced. The next usage summary uses the
   * new table.
   */
  serverRefreshUsageRates: Rpc.make(WS_METHODS.serverRefreshUsageRates, {
    payload: Schema.Struct({}),
    success: UsagePricing,
    error: ProviderUsageLimitsError,
  }),
  serverProviderConsumeResetCredit: Rpc.make(WS_METHODS.serverProviderConsumeResetCredit, {
    payload: ProviderConsumeResetCreditInput,
    success: ProviderConsumeResetCreditResult,
    error: ProviderUsageLimitsError,
  }),

  serverUpdateProvider: Rpc.make(WS_METHODS.serverUpdateProvider, {
    payload: ServerProviderUpdateInput,
    success: ServerProviderUpdateResult,
    error: ServerProviderUpdateError,
  }),

  serverListProviderSkills: Rpc.make(WS_METHODS.serverListProviderSkills, {
    payload: ServerProviderSkillCatalogInput,
    success: ServerProviderSkillCatalogResult,
    error: Schema.Union([ServerProviderSkillCatalogError]),
  }),

  serverUpdateSettings: Rpc.make(WS_METHODS.serverUpdateSettings, {
    payload: Schema.Struct({ patch: ServerSettingsPatch }),
    success: ServerSettings,
    error: Schema.Union([
      ServerSettingsError,
      ServerSettingsHostOnlyError,
      ServerSettingsManagedError,
    ]),
  }),

  serverDiscoverSourceControl: Rpc.make(WS_METHODS.serverDiscoverSourceControl, {
    payload: Schema.Struct({}),
    success: SourceControlDiscoveryResult,
  }),

  serverGitHubAuthenticationStatus: Rpc.make(WS_METHODS.serverGitHubAuthenticationStatus, {
    payload: Schema.Struct({}),
    success: GitHubAuthenticationStatus,
  }),
  serverGitHubAuthenticationStart: Rpc.make(WS_METHODS.serverGitHubAuthenticationStart, {
    payload: Schema.Struct({ commandId: CommandId }),
    success: GitHubAuthenticationStartResult,
    error: GitHubAuthenticationError,
  }),
  serverGitHubAuthenticationCancel: Rpc.make(WS_METHODS.serverGitHubAuthenticationCancel, {
    payload: Schema.Struct({ attemptId: CommandId }),
    success: GitHubAuthenticationStatus,
    error: GitHubAuthenticationError,
  }),
  serverGitHubAuthenticationDisconnect: Rpc.make(WS_METHODS.serverGitHubAuthenticationDisconnect, {
    payload: Schema.Struct({ account: TrimmedNonEmptyString }),
    success: GitHubAuthenticationStatus,
    error: GitHubAuthenticationError,
  }),

  serverGetTraceDiagnostics: Rpc.make(WS_METHODS.serverGetTraceDiagnostics, {
    payload: Schema.Struct({}),
    success: ServerTraceDiagnosticsResult,
  }),

  serverGetProcessDiagnostics: Rpc.make(WS_METHODS.serverGetProcessDiagnostics, {
    payload: Schema.Struct({}),
    success: ServerProcessDiagnosticsResult,
  }),

  serverGetHostResources: Rpc.make(WS_METHODS.serverGetHostResources, {
    payload: Schema.Struct({}),
    success: HostResourcesSnapshot,
  }),

  // Guest lifecycle commands are authorized by explicit Connect grants on their parent.
  guestEnvironmentsCapabilities: Rpc.make(WS_METHODS.guestEnvironmentsCapabilities, {
    payload: Schema.Struct({}),
    success: GuestEnvironmentCapabilities,
    error: GuestEnvironmentError,
  }),
  guestEnvironmentsList: Rpc.make(WS_METHODS.guestEnvironmentsList, {
    payload: Schema.Struct({}),
    success: GuestEnvironmentList,
    error: GuestEnvironmentError,
  }),
  guestEnvironmentsCreate: Rpc.make(WS_METHODS.guestEnvironmentsCreate, {
    payload: Schema.Struct({ input: CreateGuestEnvironment, grant: GuestProvisioningGrant }),
    success: GuestEnvironment,
    error: GuestEnvironmentError,
  }),
  guestEnvironmentsAction: Rpc.make(WS_METHODS.guestEnvironmentsAction, {
    payload: Schema.Struct({ input: GuestEnvironmentAction, grant: GuestActionGrant }),
    success: GuestEnvironment,
    error: GuestEnvironmentError,
  }),

  machinePoolList: Rpc.make(WS_METHODS.machinePoolList, {
    payload: Schema.Struct({ projectId: Schema.String }),
    success: Schema.Array(MachinePoolEntryView),
    error: MachinePoolError,
  }),
  machinePoolAdd: Rpc.make(WS_METHODS.machinePoolAdd, {
    payload: MachinePoolAddInput,
    success: MachinePoolEntryView,
    error: MachinePoolError,
  }),
  machinePoolRemove: Rpc.make(WS_METHODS.machinePoolRemove, {
    payload: Schema.Struct({ projectId: Schema.String, environmentId: Schema.String }),
    /** `revoked` is false when the destination could not be reached to sign the session out. */
    success: Schema.Struct({ revoked: Schema.Boolean }),
    error: MachinePoolError,
  }),
  machinePoolIssueDelegation: Rpc.make(WS_METHODS.machinePoolIssueDelegation, {
    /** The project here the pool's threads start in; the grant acts in it alone. */
    payload: Schema.Struct({ projectId: Schema.String }),
    success: Schema.Struct({ grant: Schema.String, expiresAt: Schema.Number }),
    error: MachinePoolError,
  }),

  serverGetProcessResourceHistory: Rpc.make(WS_METHODS.serverGetProcessResourceHistory, {
    payload: ServerProcessResourceHistoryInput,
    success: ServerProcessResourceHistoryResult,
  }),

  serverSignalProcess: Rpc.make(WS_METHODS.serverSignalProcess, {
    payload: ServerSignalProcessInput,
    success: ServerSignalProcessResult,
  }),

  sourceControlLookupRepository: Rpc.make(WS_METHODS.sourceControlLookupRepository, {
    payload: SourceControlRepositoryLookupInput,
    success: SourceControlRepositoryInfo,
    error: Schema.Union([SourceControlRepositoryError]),
  }),

  sourceControlCloneRepository: Rpc.make(WS_METHODS.sourceControlCloneRepository, {
    payload: SourceControlCloneRepositoryInput,
    success: SourceControlCloneRepositoryResult,
    error: Schema.Union([SourceControlRepositoryError]),
  }),

  sourceControlPublishRepository: Rpc.make(WS_METHODS.sourceControlPublishRepository, {
    payload: SourceControlPublishRepositoryInput,
    success: SourceControlPublishRepositoryResult,
    error: Schema.Union([SourceControlRepositoryError]),
  }),

  projectsSearchEntries: Rpc.make(WS_METHODS.projectsSearchEntries, {
    payload: ProjectSearchEntriesInput,
    success: ProjectSearchEntriesResult,
    error: Schema.Union([ProjectSearchEntriesError]),
  }),

  projectsDiscoverDesignSystemMentions: Rpc.make(WS_METHODS.projectsDiscoverDesignSystemMentions, {
    payload: ProjectDiscoverDesignSystemMentionsInput,
    success: ProjectDiscoverDesignSystemMentionsResult,
    error: Schema.Union([ProjectDiscoverDesignSystemMentionsError]),
  }),

  projectsListEntries: Rpc.make(WS_METHODS.projectsListEntries, {
    payload: ProjectListEntriesInput,
    success: ProjectListEntriesResult,
    error: Schema.Union([ProjectListEntriesError]),
  }),

  projectsRefreshEntries: Rpc.make(WS_METHODS.projectsRefreshEntries, {
    payload: ProjectRefreshEntriesInput,
    success: ProjectRefreshEntriesResult,
    error: Schema.Union([ProjectRefreshEntriesError]),
  }),

  projectsCreateAfar: Rpc.make(WS_METHODS.projectsCreateAfar, {
    payload: ProjectCreateAfarInput,
    success: ProjectInspectAfarResult,
    error: Schema.Union([ProjectCreateAfarError]),
  }),

  projectsAssessAfar: Rpc.make(WS_METHODS.projectsAssessAfar, {
    payload: ProjectAssessAfarInput,
    success: ProjectAssessAfarResult,
  }),

  projectsInspectAfar: Rpc.make(WS_METHODS.projectsInspectAfar, {
    payload: ProjectInspectAfarInput,
    success: ProjectInspectAfarResult,
    error: Schema.Union([ProjectInspectAfarError]),
  }),

  subscribeProjectActionReadiness: Rpc.make(WS_METHODS.subscribeProjectActionReadiness, {
    payload: Schema.Struct({}),
    success: ProjectActionReadinessEvent,
    stream: true,
  }),

  projectsReadFile: Rpc.make(WS_METHODS.projectsReadFile, {
    payload: ProjectReadFileInput,
    success: ProjectReadFileResult,
    error: Schema.Union([ProjectReadFileError]),
  }),

  contributionsQuery: Rpc.make(WS_METHODS.contributionsQuery, {
    payload: ContributionsQueryInput,
    success: ContributionsQueryResult,
    error: Schema.Union([ContributionsError]),
  }),
  projectContributionGenerate: Rpc.make(WS_METHODS.projectContributionGenerate, {
    payload: ProjectContributionGenerationInput,
    success: ProjectContributionGeneration,
    error: ProjectContributionGenerationError,
  }),
  projectContributionGenerationEvents: Rpc.make(WS_METHODS.projectContributionGenerationEvents, {
    payload: Schema.Struct({ projectId: ProjectId }),
    success: Schema.NullOr(ProjectContributionGeneration),
    error: ProjectContributionGenerationError,
    stream: true,
  }),
  projectContributionCancel: Rpc.make(WS_METHODS.projectContributionCancel, {
    payload: Schema.Struct({
      projectId: ProjectId,
      id: TrimmedNonEmptyString,
      revision: NonNegativeInt,
    }),
    success: ProjectContributionGeneration,
    error: ProjectContributionGenerationError,
  }),

  contributionsExportAgentTrace: Rpc.make(WS_METHODS.contributionsExportAgentTrace, {
    payload: ContributionsExportInput,
    success: ContributionsExportResult,
    error: Schema.Union([ContributionsError]),
  }),

  projectsWriteFile: Rpc.make(WS_METHODS.projectsWriteFile, {
    payload: ProjectWriteFileInput,
    success: ProjectWriteFileResult,
    error: Schema.Union([ProjectWriteFileError]),
  }),

  /** The file's content digest now, then again each time it changes on disk. */
  projectsSubscribeFile: Rpc.make(WS_METHODS.projectsSubscribeFile, {
    payload: ProjectReadFileInput,
    success: ProjectFileChange,
    error: Schema.Union([ProjectReadFileError]),
    stream: true,
  }),

  scheduleListDefinitions: Rpc.make(WS_METHODS.scheduleListDefinitions, {
    payload: ScheduleListDefinitionsInput,
    success: ScheduleListDefinitionsResult,
    error: Schema.Union([ScheduleDefinitionError]),
  }),

  scheduleSaveDefinition: Rpc.make(WS_METHODS.scheduleSaveDefinition, {
    payload: ScheduleSaveDefinitionInput,
    success: ScheduleSaveDefinitionResult,
    error: Schema.Union([ScheduleDefinitionError]),
  }),

  scheduleDeleteDefinition: Rpc.make(WS_METHODS.scheduleDeleteDefinition, {
    payload: ScheduleDeleteDefinitionInput,
    success: ScheduleDeleteDefinitionResult,
    error: Schema.Union([ScheduleDefinitionError]),
  }),

  scheduleListActivations: Rpc.make(WS_METHODS.scheduleListActivations, {
    payload: ScheduleListActivationsInput,
    success: ScheduleListActivationsResult,
    error: Schema.Union([ScheduleActivationError]),
  }),

  scheduleListOccurrences: Rpc.make(WS_METHODS.scheduleListOccurrences, {
    payload: ScheduleListOccurrencesInput,
    success: ScheduleListOccurrencesResult,
    error: Schema.Union([ScheduleOccurrenceError]),
  }),

  scheduleSaveAndEnable: Rpc.make(WS_METHODS.scheduleSaveAndEnable, {
    payload: ScheduleSaveAndEnableInput,
    success: ScheduleSaveAndEnableResult,
    error: Schema.Union([ScheduleActivationError, ScheduleDefinitionError]),
  }),

  scheduleSetActivationDesiredState: Rpc.make(WS_METHODS.scheduleSetActivationDesiredState, {
    payload: ScheduleSetActivationDesiredStateInput,
    success: ScheduleSetActivationDesiredStateResult,
    error: Schema.Union([ScheduleActivationError]),
  }),

  scheduleDeleteActivation: Rpc.make(WS_METHODS.scheduleDeleteActivation, {
    payload: ScheduleDeleteActivationInput,
    success: ScheduleDeleteActivationResult,
    error: Schema.Union([ScheduleActivationError]),
  }),

  scheduleDeleteOccurrence: Rpc.make(WS_METHODS.scheduleDeleteOccurrence, {
    payload: ScheduleDeleteOccurrenceInput,
    success: ScheduleDeleteOccurrenceResult,
    error: Schema.Union([ScheduleOccurrenceError]),
  }),

  scheduleListRuns: Rpc.make(WS_METHODS.scheduleListRuns, {
    payload: ScheduleListRunsInput,
    success: ScheduleListRunsResult,
    error: Schema.Union([ScheduleRunError]),
  }),

  scheduleRunNow: Rpc.make(WS_METHODS.scheduleRunNow, {
    payload: ScheduleRunNowInput,
    success: ScheduleRunNowResult,
    error: Schema.Union([ScheduleRunError]),
  }),

  scheduleCancelRun: Rpc.make(WS_METHODS.scheduleCancelRun, {
    payload: ScheduleCancelRunInput,
    success: ScheduleRunReadModel,
    error: Schema.Union([ScheduleRunError]),
  }),

  scheduleResolveRun: Rpc.make(WS_METHODS.scheduleResolveRun, {
    payload: ScheduleResolveRunInput,
    success: ScheduleRunReadModel,
    error: Schema.Union([ScheduleRunError]),
  }),

  designListSystems: Rpc.make(WS_METHODS.designListSystems, {
    payload: DesignListSystemsInput,
    success: DesignListSystemsResult,
    error: Schema.Union([DesignSystemError]),
  }),

  designDeleteSystem: Rpc.make(WS_METHODS.designDeleteSystem, {
    payload: DesignDeleteSystemInput,
    success: DesignDeleteSystemResult,
    error: Schema.Union([DesignSystemError]),
  }),

  designDeleteCollectionGroup: Rpc.make(WS_METHODS.designDeleteCollectionGroup, {
    payload: DesignDeleteCollectionGroupInput,
    success: DesignDeleteCollectionGroupResult,
    error: Schema.Union([DesignSystemError]),
  }),

  designDeleteInvalidSystem: Rpc.make(WS_METHODS.designDeleteInvalidSystem, {
    payload: DesignDeleteInvalidSystemInput,
    success: DesignDeleteInvalidSystemResult,
    error: Schema.Union([DesignSystemError]),
  }),

  /**
   * The Delete Design Operation's one send, and the only delete any client
   * makes. The three single-target deletes above are the primitives the server
   * composes it from, addressable on their own so one target's failure stays
   * one target's failure.
   */
  designDeleteTargets: Rpc.make(WS_METHODS.designDeleteTargets, {
    payload: DesignDeleteTargetsInput,
    success: DesignDeleteTargetsResult,
    error: Schema.Union([DesignSystemError]),
  }),

  experimentQueryEvidence: Rpc.make(WS_METHODS.experimentQueryEvidence, {
    payload: ExperimentEvidenceQueryInput,
    success: ExperimentEvidenceQueryResult,
    error: Schema.Union([ExperimentEvidenceQueryError]),
  }),

  experimentQueryWorkspace: Rpc.make(WS_METHODS.experimentQueryWorkspace, {
    payload: ExperimentWorkspaceQueryInput,
    success: ExperimentWorkspaceQueryResult,
    error: Schema.Union([ExperimentWorkspaceQueryError]),
  }),

  workflowSubscribe: Rpc.make(WS_METHODS.workflowSubscribe, {
    payload: WorkflowSubscribeInput,
    success: WorkflowStreamEvent,
    error: WorkflowError,
    stream: true,
  }),
  workflowSave: Rpc.make(WS_METHODS.workflowSave, {
    payload: WorkflowSaveInput,
    success: WorkflowSaveResult,
    error: WorkflowError,
  }),
  workflowSetValues: Rpc.make(WS_METHODS.workflowSetValues, {
    payload: WorkflowSetValuesInput,
    success: WorkflowSetValuesResult,
    error: WorkflowError,
  }),
  workflowEnqueueRun: Rpc.make(WS_METHODS.workflowEnqueueRun, {
    payload: WorkflowEnqueueRunInput,
    success: WorkflowRun,
    error: WorkflowError,
  }),
  workflowCancelRun: Rpc.make(WS_METHODS.workflowCancelRun, {
    payload: WorkflowRunIdInput,
    success: WorkflowRun,
    error: WorkflowError,
  }),
  workflowRemoveRun: Rpc.make(WS_METHODS.workflowRemoveRun, {
    payload: WorkflowRunIdInput,
    error: WorkflowError,
  }),
  workflowReadOutput: Rpc.make(WS_METHODS.workflowReadOutput, {
    payload: WorkflowReadOutputInput,
    success: WorkflowReadOutputResult,
    error: WorkflowError,
  }),

  shellOpenInEditor: Rpc.make(WS_METHODS.shellOpenInEditor, {
    payload: LaunchEditorInput,
    error: Schema.Union([ExternalLauncherError]),
  }),

  filesystemBrowse: Rpc.make(WS_METHODS.filesystemBrowse, {
    payload: FilesystemBrowseInput,
    success: FilesystemBrowseResult,
    error: Schema.Union([FilesystemBrowseError]),
  }),

  assetsCreateUrl: Rpc.make(WS_METHODS.assetsCreateUrl, {
    payload: AssetCreateUrlInput,
    success: AssetCreateUrlResult,
    error: Schema.Union([AssetAccessError]),
  }),

  attachmentsCreateUploadUrl: Rpc.make(WS_METHODS.attachmentsCreateUploadUrl, {
    payload: AttachmentCreateUploadUrlInput,
    success: AttachmentCreateUploadUrlResult,
    error: Schema.Union([AttachmentUploadSigningKeyError]),
  }),

  attachmentsDelete: Rpc.make(WS_METHODS.attachmentsDelete, {
    payload: AttachmentDeleteInput,
  }),

  subscribeVcsStatus: Rpc.make(WS_METHODS.subscribeVcsStatus, {
    payload: VcsStatusInput,
    success: VcsStatusStreamEvent,
    error: Schema.Union([GitManagerServiceError]),
    stream: true,
  }),

  vcsPull: Rpc.make(WS_METHODS.vcsPull, {
    payload: VcsPullInput,
    success: VcsPullResult,
    error: Schema.Union([GitCommandError]),
  }),

  vcsRefreshStatus: Rpc.make(WS_METHODS.vcsRefreshStatus, {
    payload: VcsStatusInput,
    success: VcsStatusResult,
    error: Schema.Union([GitManagerServiceError]),
  }),

  gitRunStackedAction: Rpc.make(WS_METHODS.gitRunStackedAction, {
    payload: GitRunStackedActionInput,
    success: GitActionProgressEvent,
    error: Schema.Union([GitManagerServiceError]),
    stream: true,
  }),

  gitResolvePullRequest: Rpc.make(WS_METHODS.gitResolvePullRequest, {
    payload: GitPullRequestRefInput,
    success: GitResolvePullRequestResult,
    error: Schema.Union([GitManagerServiceError]),
  }),

  gitPreparePullRequestThread: Rpc.make(WS_METHODS.gitPreparePullRequestThread, {
    payload: GitPreparePullRequestThreadInput,
    success: GitPreparePullRequestThreadResult,
    error: Schema.Union([GitManagerServiceError]),
  }),

  vcsListRefs: Rpc.make(WS_METHODS.vcsListRefs, {
    payload: VcsListRefsInput,
    success: VcsListRefsResult,
    error: Schema.Union([GitCommandError]),
  }),

  vcsCreateWorktree: Rpc.make(WS_METHODS.vcsCreateWorktree, {
    payload: VcsCreateWorktreeInput,
    success: VcsCreateWorktreeResult,
    error: Schema.Union([GitCommandError]),
  }),

  vcsRemoveWorktree: Rpc.make(WS_METHODS.vcsRemoveWorktree, {
    payload: VcsRemoveWorktreeInput,
    error: Schema.Union([GitCommandError]),
  }),

  vcsCreateRef: Rpc.make(WS_METHODS.vcsCreateRef, {
    payload: VcsCreateRefInput,
    success: VcsCreateRefResult,
    error: Schema.Union([GitCommandError]),
  }),

  vcsSwitchRef: Rpc.make(WS_METHODS.vcsSwitchRef, {
    payload: VcsSwitchRefInput,
    success: VcsSwitchRefResult,
    error: Schema.Union([GitCommandError]),
  }),

  vcsInit: Rpc.make(WS_METHODS.vcsInit, {
    payload: VcsInitInput,
    error: Schema.Union([VcsError]),
  }),

  worktreesListAvailable: Rpc.make(WS_METHODS.worktreesListAvailable, {
    payload: AvailableProjectWorktreesInput,
    success: AvailableProjectWorktreesResult,
    error: ManagedThreadWorktreeError,
  }),

  worktreesCreate: Rpc.make(WS_METHODS.worktreesCreate, {
    payload: ManagedThreadWorktreeCreateInput,
    success: ManagedThreadWorktreeCreateResult,
    error: ManagedThreadWorktreeError,
  }),

  worktreesListManaged: Rpc.make(WS_METHODS.worktreesListManaged, {
    payload: ManagedThreadWorktreesListInput,
    success: ManagedThreadWorktreesListResult,
    error: ManagedThreadWorktreeError,
  }),

  worktreesDelete: Rpc.make(WS_METHODS.worktreesDelete, {
    payload: ManagedThreadWorktreeDeleteInput,
    success: ManagedThreadWorktreeDeleteResult,
  }),

  worktreesDeleteProject: Rpc.make(WS_METHODS.worktreesDeleteProject, {
    payload: ManagedThreadWorktreeDeleteProjectInput,
    success: ManagedThreadWorktreeDeleteResult,
    error: ManagedThreadWorktreeError,
  }),

  worktreesDeleteAll: Rpc.make(WS_METHODS.worktreesDeleteAll, {
    payload: ManagedThreadWorktreeDeleteAllInput,
    success: ManagedThreadWorktreeDeleteResult,
    error: ManagedThreadWorktreeError,
  }),

  worktreesListEvidenceRecoveries: Rpc.make(WS_METHODS.worktreesListEvidenceRecoveries, {
    payload: ManagedThreadWorktreeListEvidenceRecoveriesInput,
    success: ManagedThreadWorktreeListEvidenceRecoveriesResult,
    error: ManagedThreadWorktreeEvidenceRecoveryError,
  }),

  worktreesRestoreEvidence: Rpc.make(WS_METHODS.worktreesRestoreEvidence, {
    payload: ManagedThreadWorktreeRestoreEvidenceInput,
    success: ManagedThreadWorktreeRestoreEvidenceResult,
    error: ManagedThreadWorktreeEvidenceRecoveryError,
  }),

  worktreesDiscardEvidence: Rpc.make(WS_METHODS.worktreesDiscardEvidence, {
    payload: ManagedThreadWorktreeDiscardEvidenceInput,
    success: ManagedThreadWorktreeDiscardEvidenceResult,
    error: ManagedThreadWorktreeEvidenceRecoveryError,
  }),

  /**
   * Ephemeral live diff preview for compact surfaces.
   * Not the persisted AFAR Research Review model. Future review sessions should use
   * review.open* + review.getSnapshot.
   */
  reviewGetDiffPreview: Rpc.make(WS_METHODS.reviewGetDiffPreview, {
    payload: ReviewDiffPreviewInput,
    success: ReviewDiffPreviewResult,
    error: Schema.Union([ReviewDiffPreviewError]),
  }),

  terminalOpen: Rpc.make(WS_METHODS.terminalOpen, {
    payload: TerminalOpenInput,
    success: TerminalSessionSnapshot,
    error: Schema.Union([TerminalError]),
  }),

  terminalAttach: Rpc.make(WS_METHODS.terminalAttach, {
    payload: TerminalAttachInput,
    success: TerminalAttachStreamEvent,
    error: Schema.Union([TerminalError]),
    stream: true,
  }),

  terminalWrite: Rpc.make(WS_METHODS.terminalWrite, {
    payload: TerminalWriteInput,
    error: Schema.Union([TerminalError]),
  }),

  terminalResize: Rpc.make(WS_METHODS.terminalResize, {
    payload: TerminalResizeInput,
    error: Schema.Union([TerminalError]),
  }),

  terminalClear: Rpc.make(WS_METHODS.terminalClear, {
    payload: TerminalClearInput,
    error: Schema.Union([TerminalError]),
  }),

  terminalRestart: Rpc.make(WS_METHODS.terminalRestart, {
    payload: TerminalRestartInput,
    success: TerminalSessionSnapshot,
    error: Schema.Union([TerminalError]),
  }),

  terminalClose: Rpc.make(WS_METHODS.terminalClose, {
    payload: TerminalCloseInput,
    error: Schema.Union([TerminalError]),
  }),

  previewOpen: Rpc.make(WS_METHODS.previewOpen, {
    payload: PreviewOpenInput,
    success: PreviewSessionSnapshot,
    error: Schema.Union([PreviewError]),
  }),

  previewNavigate: Rpc.make(WS_METHODS.previewNavigate, {
    payload: PreviewNavigateInput,
    success: PreviewSessionSnapshot,
    error: Schema.Union([PreviewError]),
  }),

  previewResize: Rpc.make(WS_METHODS.previewResize, {
    payload: PreviewResizeInput,
    success: PreviewSessionSnapshot,
    error: Schema.Union([PreviewError]),
  }),

  previewRefresh: Rpc.make(WS_METHODS.previewRefresh, {
    payload: PreviewRefreshInput,
    error: Schema.Union([PreviewError]),
  }),

  previewClose: Rpc.make(WS_METHODS.previewClose, {
    payload: PreviewCloseInput,
    error: Schema.Union([PreviewError]),
  }),

  previewList: Rpc.make(WS_METHODS.previewList, {
    payload: PreviewListInput,
    success: PreviewListResult,
  }),

  previewReportStatus: Rpc.make(WS_METHODS.previewReportStatus, {
    payload: PreviewReportStatusInput,
    error: Schema.Union([PreviewError]),
  }),

  // Driven in the Environment's own browser (ADR-0137); a Desktop Client's
  // Electron browser handles these through its bridge instead.
  previewHistory: Rpc.make(WS_METHODS.previewHistory, {
    payload: PreviewHistoryInput,
    error: PreviewBrowserError,
  }),

  previewSetZoom: Rpc.make(WS_METHODS.previewSetZoom, {
    payload: PreviewSetZoomInput,
    error: PreviewBrowserError,
  }),

  previewSetColorScheme: Rpc.make(WS_METHODS.previewSetColorScheme, {
    payload: PreviewSetColorSchemeInput,
    error: PreviewBrowserError,
  }),

  previewClearBrowsingData: Rpc.make(WS_METHODS.previewClearBrowsingData, {
    payload: PreviewClearBrowsingDataInput,
    error: BrowserProfileError,
  }),

  // Answers when the user submits or cancels the overlay, or the page navigates.
  previewPickElement: Rpc.make(WS_METHODS.previewPickElement, {
    payload: PreviewPickElementInput,
    success: Schema.NullOr(PreviewAnnotationSubmissionResultSchema),
    error: PreviewBrowserError,
  }),

  previewCancelPick: Rpc.make(WS_METHODS.previewCancelPick, {
    payload: PreviewCancelPickInput,
  }),

  // Starting and stopping is an ordinary Settings patch; this stream carries
  // only what the supervisor achieved, which Settings cannot know.
  computerUseServiceStatus: Rpc.make(WS_METHODS.computerUseServiceStatus, {
    success: ComputerUseServiceStatus,
    stream: true,
  }),

  // Re-running the probes is the only way to learn that a grant landed: macOS
  // never tells the process it changed its mind.
  computerUseServiceRecheck: Rpc.make(WS_METHODS.computerUseServiceRecheck, {
    success: ComputerUseServiceStatus,
    error: ComputerUseServiceError,
  }),

  computerUseServiceRequestPermissions: Rpc.make(WS_METHODS.computerUseServiceRequestPermissions, {
    success: ComputerUseServiceStatus,
    error: ComputerUseServiceError,
  }),

  // The pane opens on the Environment's own host, so this is the host's to do
  // rather than the client's: a Desktop shell and a loopback browser both
  // reach it the same way, and a paired remote client is refused.
  computerUseServiceOpenPrivacySettings: Rpc.make(
    WS_METHODS.computerUseServiceOpenPrivacySettings,
    {
      payload: MacosPrivacyPane,
      success: Schema.Void,
      error: ComputerUseServiceError,
    },
  ),

  previewAutomationConnect: Rpc.make(WS_METHODS.previewAutomationConnect, {
    payload: PreviewAutomationHost,
    success: PreviewAutomationStreamEvent,
    error: Schema.Union([PreviewAutomationError]),
    stream: true,
  }),

  previewAutomationRespond: Rpc.make(WS_METHODS.previewAutomationRespond, {
    payload: PreviewAutomationResponse,
    error: Schema.Union([PreviewAutomationError]),
  }),

  previewAutomationFocusHost: Rpc.make(WS_METHODS.previewAutomationFocusHost, {
    payload: PreviewAutomationHostFocus,
  }),

  subscribePreviewEvents: Rpc.make(WS_METHODS.subscribePreviewEvents, {
    payload: Schema.Struct({}),
    success: PreviewEvent,
    stream: true,
  }),

  subscribeDiscoveredLocalServers: Rpc.make(WS_METHODS.subscribeDiscoveredLocalServers, {
    payload: Schema.Struct({
      configuredUrls: Schema.optional(ConfiguredLocalServerUrls),
    }),
    success: DiscoveredLocalServerList,
    stream: true,
  }),

  dispatchCommand: Rpc.make(ORCHESTRATION_WS_METHODS.dispatchCommand, {
    payload: ClientOrchestrationCommand,
    success: OrchestrationRpcSchemas.dispatchCommand.output,
    error: Schema.Union([
      OrchestrationDispatchCommandError,
      EnvironmentCommandIdempotencyConflictError,
      EnvironmentCommandRejectedError,
    ]),
  }),

  getTurnDiff: Rpc.make(ORCHESTRATION_WS_METHODS.getTurnDiff, {
    payload: OrchestrationGetTurnDiffInput,
    success: OrchestrationRpcSchemas.getTurnDiff.output,
    error: Schema.Union([OrchestrationGetTurnDiffError]),
  }),

  getFullThreadDiff: Rpc.make(ORCHESTRATION_WS_METHODS.getFullThreadDiff, {
    payload: OrchestrationGetFullThreadDiffInput,
    success: OrchestrationRpcSchemas.getFullThreadDiff.output,
    error: Schema.Union([OrchestrationGetFullThreadDiffError]),
  }),

  replayEvents: Rpc.make(ORCHESTRATION_WS_METHODS.replayEvents, {
    payload: OrchestrationReplayEventsInput,
    success: OrchestrationRpcSchemas.replayEvents.output,
    error: Schema.Union([OrchestrationReplayEventsError]),
  }),

  getArchivedShellSnapshot: Rpc.make(ORCHESTRATION_WS_METHODS.getArchivedShellSnapshot, {
    payload: OrchestrationRpcSchemas.getArchivedShellSnapshot.input,
    success: OrchestrationRpcSchemas.getArchivedShellSnapshot.output,
    error: Schema.Union([OrchestrationGetSnapshotError]),
  }),

  synchronizeHostView: Rpc.make(ORCHESTRATION_WS_METHODS.synchronizeHostView, {
    payload: OrchestrationRpcSchemas.synchronizeHostView.input,
    success: OrchestrationRpcSchemas.synchronizeHostView.output,
    error: Schema.Union([OrchestrationGetSnapshotError]),
    stream: true,
  }),

  subscribeThread: Rpc.make(ORCHESTRATION_WS_METHODS.subscribeThread, {
    payload: OrchestrationRpcSchemas.subscribeThread.input,
    success: OrchestrationRpcSchemas.subscribeThread.output,
    error: Schema.Union([OrchestrationGetSnapshotError, OrchestrationThreadHistoryError]),
    stream: true,
  }),

  subscribeTerminalEvents: Rpc.make(WS_METHODS.subscribeTerminalEvents, {
    payload: Schema.Struct({}),
    success: TerminalEvent,
    stream: true,
  }),

  subscribeTerminalMetadata: Rpc.make(WS_METHODS.subscribeTerminalMetadata, {
    payload: Schema.Struct({}),
    success: TerminalMetadataStreamEvent,
    stream: true,
  }),

  subscribeServerConfig: Rpc.make(WS_METHODS.subscribeServerConfig, {
    payload: Schema.Struct({}),
    success: ServerConfigStreamEvent,
    error: Schema.Union([KeybindingsConfigError, ServerSettingsError]),
    stream: true,
  }),

  subscribeServerLifecycle: Rpc.make(WS_METHODS.subscribeServerLifecycle, {
    payload: Schema.Struct({}),
    success: ServerLifecycleStreamEvent,
    stream: true,
  }),

  pullRequestsList: Rpc.make(WS_METHODS.pullRequestsList, {
    payload: PullRequestListInput,
    success: PullRequestListResult,
  }),
  pullRequestsListStats: Rpc.make(WS_METHODS.pullRequestsListStats, {
    payload: PullRequestListStatsInput,
    success: PullRequestListStatsResult,
  }),
  pullRequestsChecks: Rpc.make(WS_METHODS.pullRequestsChecks, {
    payload: PullRequestChecksInput,
    success: PullRequestChecksResult,
  }),
  pullRequestsDiscuss: Rpc.make(WS_METHODS.pullRequestsDiscuss, {
    payload: PullRequestDiscussionInput,
    success: PullRequestEditResult,
  }),
  pullRequestsEdit: Rpc.make(WS_METHODS.pullRequestsEdit, {
    payload: PullRequestEditInput,
    success: PullRequestEditResult,
  }),
  pullRequestsDetail: Rpc.make(WS_METHODS.pullRequestsDetail, {
    payload: PullRequestDetailInput,
    success: PullRequestDetailResult,
  }),
  pullRequestsCode: Rpc.make(WS_METHODS.pullRequestsCode, {
    payload: PullRequestCodeInput,
    success: PullRequestCodeResult,
  }),
  pullRequestsTimeline: Rpc.make(WS_METHODS.pullRequestsTimeline, {
    payload: PullRequestTimelineInput,
    success: PullRequestTimelineResult,
  }),
  pullRequestsLinkedThreads: Rpc.make(WS_METHODS.pullRequestsLinkedThreads, {
    payload: PullRequestLinkedThreadsInput,
    success: PullRequestLinkedThreadsResult,
    error: PullRequestLinkedThreadsError,
  }),
  getThreadHistoryPage: Rpc.make(ORCHESTRATION_WS_METHODS.getThreadHistoryPage, {
    payload: OrchestrationRpcSchemas.getThreadHistoryPage.input,
    success: OrchestrationRpcSchemas.getThreadHistoryPage.output,
    error: Schema.Union([OrchestrationThreadHistoryError]),
  }),
  synchronizeHostViewOptimized: Rpc.make(ORCHESTRATION_WS_METHODS.synchronizeHostViewOptimized, {
    payload: OrchestrationRpcSchemas.synchronizeHostViewOptimized.input,
    success: OrchestrationRpcSchemas.synchronizeHostViewOptimized.output,
    error: Schema.Union([OrchestrationShellBootstrapError]),
    stream: true,
  }),
} as const;

/**
 * Derived from `WsRpcs`; the method list is not restated here.
 *
 * `RpcGroup.make` is typed as `RpcGroup<Rpcs[number]>`, so passing the
 * object's values keeps the exact per-method typing a positional list gave.
 */
export const WsRpcGroup = RpcGroup.make(...Object.values(WsRpcs));

export const WsPullRequestsListRpc = WsRpcs.pullRequestsList;
export const WsPullRequestsListStatsRpc = WsRpcs.pullRequestsListStats;
export const WsPullRequestsChecksRpc = WsRpcs.pullRequestsChecks;
export const WsPullRequestsDiscussRpc = WsRpcs.pullRequestsDiscuss;
export const WsPullRequestsEditRpc = WsRpcs.pullRequestsEdit;
export const WsPullRequestsDetailRpc = WsRpcs.pullRequestsDetail;
export const WsPullRequestsCodeRpc = WsRpcs.pullRequestsCode;
export const WsPullRequestsTimelineRpc = WsRpcs.pullRequestsTimeline;
export const WsPullRequestsLinkedThreadsRpc = WsRpcs.pullRequestsLinkedThreads;
export const WsOrchestrationGetThreadHistoryPageRpc = WsRpcs.getThreadHistoryPage;
export const WsOrchestrationSynchronizeHostViewOptimizedRpc = WsRpcs.synchronizeHostViewOptimized;
